import { useState, useEffect } from 'react'
import _ from 'lodash'
import useTranslate from '../../../i18n/useTranslate'
import { makeStyles } from '@material-ui/core'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { FirstTimeDataListType } from '../../../services/um/useSearchFirstTime'
import { convertDateToFormatTH, convertDateToFormatEN } from '../../../utilities/date'
import { setPrefixName } from '../../../utilities/prefixName'
import CustomPagination from '../closed/CustomPagination'
import CustomNoRowsOverlay from '../closed/CustomNoRowsOverlay'
import { CustomSortingASCcending, CustomSortingDescending } from '../closed/CusromSorting'

type DataRowsType = {
    id: number
    receiveDate: string
    projectNumber: string
    projectNameTH: string
    projectNameEN: string
    projectType: string
    name: string
    titleID: number
    title: string
    academicPosition: string
    isPhD: boolean
    researcherTypeName: string
    facultyName: string
    approveStatusName: string
    projectSubmitType: string
}

type FirstTimeDataTableComponentProps = {
    dataList: FirstTimeDataListType[]
}

const DataTable = ({ dataList }: FirstTimeDataTableComponentProps) => {
    const classes = useStyles()
    const translate = useTranslate()
    const lang = translate.languageStore.currentLanguage as 'th' | 'en'
    const [firstTimeDataList, setFirstTimeDataList] = useState<DataRowsType[]>([])

    useEffect(() => {
        const convertData = dataList.map((item) => {
            return {
                id: item.projectID,
                receiveDate: convertDate(item.receiveDate),
                projectNumber: item.projectNumber,
                projectNameTH: item.projectName['th'] ?? '-',
                projectNameEN: item.projectName['en'] ?? '-',
                projectType: item.projectType.name[lang] ?? '-',
                name: setPrefixName(item)[lang] ?? '-',
                titleID: item.titleID ?? '-',
                title: item.title[lang] ?? '-',
                academicPosition: item.academicPosition[lang] ?? '-',
                isPhD: item.isPhD ?? '-',
                researcherTypeName: item.researcherTypeName[lang] ?? '-',
                facultyName: item.facultyName[lang] ?? '-',
                educationLevelName: item.educationLevelName[lang] ?? '-',
                approveStatusName: item.approveStatusName?.[lang] ?? '-',
                projectSubmitType: item.submittedTypeName?.[lang],
            }
        })

        setFirstTimeDataList(convertData)
    }, [dataList, lang])

    const convertDate = (date: string | null) => {
        if (lang === 'th' && date) {
            return convertDateToFormatTH(date)
        } else if (lang === 'en' && date) {
            return convertDateToFormatEN(date)
        } else {
            return '-'
        }
    }
    const column: GridColDef[] = [
        {
            field: 'projectNumber',
            headerName: `${translate.get('UM.FIRST_TIME.DATA_TABLE.COLUMN1')}`,
            width: 200,
            headerClassName: 'header-table',
            headerAlign: 'left',
        },
        {
            field: 'receiveDate',
            headerName: `${translate.get('UM.FIRST_TIME.DATA_TABLE.COLUMN2')}`,
            width: 200,
            headerClassName: 'header-table',
            headerAlign: 'left',
        },
        {
            field: 'projectSubmitType',
            headerName: `${translate.get('UM.FIRST_TIME.DATA_TABLE.COLUMN3')}`,
            width: 250,
            headerClassName: 'header-table',
            headerAlign: 'left',
        },
        {
            field: 'projectType',
            headerName: `${translate.get('UM.FIRST_TIME.DATA_TABLE.COLUMN4')}`,
            width: 200,
            headerClassName: 'header-table',
            headerAlign: 'left',
        },
        {
            field: 'approveStatusName',
            headerName: `${translate.get('UM.FIRST_TIME.DATA_TABLE.COLUMN5')}`,
            width: 200,
            headerClassName: 'header-table',
            headerAlign: 'left',
        },

        {
            field: 'name',
            headerName: `${translate.get('UM.FIRST_TIME.DATA_TABLE.COLUMN6')}`,
            width: 300,
            headerClassName: 'header-table',
            headerAlign: 'left',
        },
        {
            field: 'researcherTypeName',
            headerName: `${translate.get('UM.FIRST_TIME.DATA_TABLE.COLUMN7')}`,
            width: 200,
            headerClassName: 'header-table',
            headerAlign: 'left',
        },
        {
            field: 'facultyName',
            headerName: `${translate.get('UM.FIRST_TIME.DATA_TABLE.COLUMN8')}`,
            width: 300,
            headerClassName: 'header-table',
            headerAlign: 'left',
        },
        {
            field: 'educationLevelName',
            headerName: `${translate.get('UM.FIRST_TIME.DATA_TABLE.COLUMN9')}`,
            width: 300,
            headerClassName: 'header-table',
            headerAlign: 'left',
        },
        {
            field: 'projectNameTH',
            headerName: `${translate.get('UM.FIRST_TIME.DATA_TABLE.COLUMN10')}`,
            width: 300,
            headerClassName: 'header-table',
            headerAlign: 'left',
        },
        {
            field: 'projectNameEN',
            headerName: `${translate.get('UM.FIRST_TIME.DATA_TABLE.COLUMN11')}`,
            width: 300,
            headerClassName: 'header-table',
            headerAlign: 'left',
        },
    ]

    return (
        <div style={{ width: '100%', marginTop: 16 }}>
            <DataGrid
                className={classes.root}
                rows={firstTimeDataList}
                columns={column}
                ///pagination
                autoHeight={true}
                pagination
                pageSize={10}
                rowsPerPageOptions={[10]}
                sortingOrder={['asc', 'desc']}
                disableColumnMenu={true}
                disableSelectionOnClick={true}
                components={{
                    Pagination: CustomPagination,
                    NoRowsOverlay: CustomNoRowsOverlay,
                    // ColumnSortedDescendingIcon: CustomSortingDescending,
                    // ColumnSortedAscendingIcon: CustomSortingASCcending,
                }}
            />
        </div>
    )
}

const useStyles = makeStyles((theme) => ({
    root: {
        '& .super-app-theme--fffff': {
            backgroundColor: '#DB5F8E',
            '&:hover': {
                backgroundColor: '#DB5F8E',
            },
        },
        '& .super-app-theme--header': {
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
        },
        '& .MuiDataGrid-columnSeparator': {
            visibility: 'hidden',
        },
        '& .MuiDataGrid-row': {
            lineHeigh: '70px',
        },

        '& .header-table': {
            backgroundColor: '#DB5F8E',
            color: '#FFFFFF',
        },
        '& .MuiDataGrid-iconSeparator': {
            display: 'none',
        },
        '& .MuiDataGrid-sortIcon': {
            color: '#FFFFFF',
        },
        '& .MuiBadge-anchorOriginTopRightRectangle': {
            display: 'none',
        },
        '& .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
        '& .MuiDataGrid-row, .MuiDataGrid-root .MuiDataGrid-cell, .rendering-zone': { 'max-height': 'none !important' },
        '.MuiDataGrid-root .MuiDataGrid-window': { position: 'relative !important' },
        '.MuiDataGrid-root .MuiDataGrid-viewport': { 'max-height': 'none !important' },
        '.MuiDataGrid-root': { height: 'auto !important' },
        width: '100%',
        marginTop: 16,

        overflowX: 'hidden',
    },
}))

export default DataTable
